import { average, sortByKey } from '@unistocks/shared/utils/util';
import { DateRange, TimePeriod } from '@unistocks/types';
import { TimeSeriesEntry } from '@unistocks/types/time-series';
import { Nullable } from '@unistocks/types/util/nullable';
import { getPeriodEnd, getPeriodStart } from './period';

export type GroupTimeSeriesEntryKey = 'open' | 'close' | 'high' | 'low' | 'average';

export type GroupDataOptions = {
  /**
   * The time period to group the data by. If not provided, the function will try to determine the best group by value based on the data.
   * @default auto (determined by the date range of the data)
   */
  groupBy?: TimePeriod;
  /**
   * The value to use for the grouped entries. This determines which value is used for the `value` property of the grouped entries,
   * and which value is used for the y-axis when rendering charts.
   * Defaults to 'average', which means the average of all values in the group will be used.
   * If set to 'open', 'close', 'high', or 'low', the corresponding value from the grouped entries will be used.
   * @default 'average'
   */
  groupValue?: GroupTimeSeriesEntryKey;
};

export type GroupedTimeSeriesEntry<T extends TimeSeriesEntry> = T & {
  values: T[];
  startOfPeriod: number;
  endOfPeriod: number;
  open: number;
  close: number;
  high: number;
  low: number;
  average: number;
};

export const groupDataItems = <T extends TimeSeriesEntry>(
  rawData: T[],
  options: GroupDataOptions = {}
) => {
  const { groupBy = 'day', groupValue = 'average' } = options;

  const groupedData = rawData.reduce((acc, entry) => {
    const startOfPeriod = getPeriodStart(entry.timestamp, groupBy);
    const endOfPeriod = getPeriodEnd(entry.timestamp, groupBy);
    const timestamp = startOfPeriod;

    if (acc.has(timestamp)) {
      const existingGroup = acc.get(timestamp)!;
      existingGroup.values.push(entry);
      existingGroup.open = existingGroup.values.sort(sortByKey('timestamp')).at(0)!.value;
      existingGroup.close = existingGroup.values.sort(sortByKey('timestamp')).at(-1)!.value;
      existingGroup.high = Math.max(existingGroup.high, entry.value);
      existingGroup.low = Math.min(existingGroup.low, entry.value);
      existingGroup.average = average<TimeSeriesEntry>(existingGroup.values, 'value');
      existingGroup.value = existingGroup[groupValue];
    } else {
      acc.set(timestamp, {
        ...entry,
        startOfPeriod,
        endOfPeriod,
        timestamp: timestamp,
        values: [entry],
        open: entry.value,
        close: entry.value,
        high: entry.value,
        low: entry.value,
        average: entry.value
      });
    }

    return acc;
  }, new Map<number, GroupedTimeSeriesEntry<T>>());

  return Array.from(groupedData.values());
};

export const resolveGroupBy = (selectedPeriod: Nullable<DateRange>): TimePeriod => {
  if (selectedPeriod) {
    const [start, end] = selectedPeriod;
    const diff = end.diff(start, 'months');

    if (diff.months > 12) {
      return 'month';
    } else {
      return 'day';
    }
  }

  return 'month';
};
